import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { CATEGORIES, CASTELLI, CONDITIONS } from "../constants";
import ListingCard from "../components/ListingCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { ChevronRight, Filter, SlidersHorizontal } from "lucide-react";
import { listingsAPI } from "../lib/api";

export default function Category() {
  const { catId } = useParams();
  const cat = CATEGORIES.find(c => c.id === catId) || { name: "Categoria" };

  const [items, setItems] = useState([]);
  const [castello, setCastello] = useState("Tutti i Castelli");
  const [sort, setSort] = useState("recent");
  const [price, setPrice] = useState([0, 10000]);
  const [condFilter, setCondFilter] = useState([]);
  const [barterOnly, setBarterOnly] = useState(false);

  useEffect(() => {
    const params = { category: catId, sort, min_price: price[0], max_price: price[1], limit: 60 };
    if (castello && castello !== "Tutti i Castelli") params.castello = castello;
    if (barterOnly) params.barter = true;
    if (condFilter.length === 1) params.condition = condFilter[0];
    listingsAPI.list(params).then(setItems).catch(() => setItems([]));
  }, [catId, castello, sort, price, barterOnly, condFilter]);

  const filtered = useMemo(() => {
    if (condFilter.length <= 1) return items;
    return items.filter(l => condFilter.includes(l.condition));
  }, [items, condFilter]);

  const toggleCond = (c) => {
    setCondFilter(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <nav className="flex items-center gap-1 text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-[#e86a24]">Home</Link>
        <ChevronRight className="w-3 h-3"/>
        <span className="text-slate-900 font-medium">{cat.name}</span>
      </nav>

      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-[#1d3d3a]">{cat.name}</h1>
          <p className="text-sm text-slate-500">{filtered.length} annunci disponibili</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px]"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Più recenti</SelectItem>
              <SelectItem value="price_asc">Prezzo crescente</SelectItem>
              <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        <aside className="bg-white border border-slate-200 rounded-xl p-4 h-fit space-y-5">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><SlidersHorizontal className="w-4 h-4"/>Filtri</div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Castello</label>
            <Select value={castello} onValueChange={setCastello}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                {CASTELLI.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Prezzo: € {price[0]} - € {price[1]}</label>
            <Slider value={price} onValueChange={setPrice} max={10000} step={50} className="mt-2" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Condizioni</label>
            <div className="space-y-2">
              {CONDITIONS.map(c => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={condFilter.includes(c)} onCheckedChange={() => toggleCond(c)} />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={barterOnly} onCheckedChange={setBarterOnly} />
              <span>Solo annunci con baratto</span>
            </label>
          </div>
        </aside>

        <div>
          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Filter className="w-10 h-10 mx-auto text-slate-400 mb-2"/>
              <p className="text-slate-600">Nessun annuncio trovato con questi filtri.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
